import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useSocket } from "@/context/SocketContext";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/lib/membership";
import { Heart, MapPin, Ban, Flag, MessageCircle, ArrowLeft, BadgeCheck, Sparkles, Trophy, Gift, Lock, Crown } from "lucide-react";

const resetsIn = (iso) => {
  if (!iso) return "Monday";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "any moment";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `in ${d}d ${h}h` : `in ${h}h`;
};
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { VellutoAddress } from "@/components/VellutoAddress";
import { dollars } from "@/lib/giftIcons";
import { PublicSocialLinks } from "@/lib/socials";
import PrivateConfirmModal from "@/components/PrivateConfirmModal";
import { useDefocusHidden, useScreenshotKey } from "@/lib/screenshotGuard";
import { Watermark } from "@/components/Watermark";
import { toast } from "sonner";

const RANK_STYLE = [
  { ring: "border-gold text-gold", label: "1" },
  { ring: "border-zinc-300 text-zinc-300", label: "2" },
  { ring: "border-amber-700 text-amber-600", label: "3" },
];

export default function ProfileDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { showMatch } = useSocket();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [lb, setLb] = useState(null);
  const [privOpen, setPrivOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const guardHidden = useDefocusHidden();
  const viewerName = user?.display_name || "Member";
  useScreenshotKey(() => toast("Screenshots are discouraged on Velluto"));

  useEffect(() => {
    api.get(`/profiles/${id}`).then(({ data }) => setP(data)).catch(() => nav("/browse"));
    api.get(`/gifts/leaderboard/${id}`).then(({ data }) => setLb(data)).catch(() => {});
  }, [id, nav]);

  if (!p) return <div className="pt-32 text-center text-text-muted">Loading…</div>;
  const paid = isPaid(user);
  const lockedByTier = !paid;
  const discreet = (p.discreet_mode || lockedByTier) && !revealed;

  const like = async () => {
    const { data } = await api.post(`/likes/${p.id}`);
    setP({ ...p, liked_by_me: data.liked });
    if (data.matched) showMatch(data.match_user);
  };

  const superLike = async () => {
    const { data } = await api.post(`/superlikes/${p.id}`);
    setP({ ...p, liked_by_me: true });
    if (data.matched) showMatch(data.match_user);
    else toast.success(`You super-admired ${p.display_name} ✦`);
  };
  const block = async () => {
    await api.post(`/safety/block/${p.id}`);
    toast.success("Member blocked");
    nav("/browse");
  };
  const report = async () => {
    await api.post("/safety/report", { user_id: p.id, reason: "Reported from profile" });
    toast.success("Report received — our team will review");
  };

  return (
    <div className="pt-20 pb-24 max-w-6xl mx-auto px-6">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-text-muted hover:text-text-main transition-colors mb-8">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/8">
          <img src={p.photo_url} alt={p.display_name} className={`w-full h-full object-cover transition-all duration-500 ${discreet ? "blur-2xl scale-110" : guardHidden ? "blur-3xl" : ""}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
          {!discreet && <Watermark text={viewerName} />}
          {discreet && (
            lockedByTier ? (
              <button data-testid="detail-unlock" onClick={() => nav("/premium")}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/40 px-6 py-3 rounded-full backdrop-blur-md bg-black/40">
                  <Lock size={14} /> Upgrade to reveal
                </span>
              </button>
            ) : (
              <button data-testid="detail-reveal" onClick={() => setRevealed(true)}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <span className="text-xs uppercase tracking-[0.25em] text-text-main border border-white/20 px-6 py-3 rounded-full backdrop-blur-md bg-black/30">
                  Discreet mode — reveal
                </span>
              </button>
            )
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          {p.is_premium && <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-3 py-1 rounded-full">Premium member</span>}
          {p.online && <span data-testid="detail-online" className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-500/40 px-3 py-1 rounded-full"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Online now</span>}
          <h1 className="font-display text-6xl font-light mt-4 leading-none flex items-center gap-3">
            <span>{p.display_name}<span className="text-text-muted text-3xl">, {p.age}</span></span>
            <VerifiedBadge user={p} size="lg" testId="detail-id-verified" />
            {p.verified && <span data-testid="detail-verified" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-emerald-400 border border-emerald-500/40 px-3 py-1.5 rounded-full"><BadgeCheck size={14} /> Verified</span>}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <VellutoAddress user={p} testId="detail-address" className="!text-sm" />
            {p.location && <p className="flex items-center gap-2 text-text-muted"><MapPin size={14} /> {p.location}</p>}
          </div>
          {p.tagline && <p className="font-display italic text-2xl text-gold mt-6">"{p.tagline}"</p>}
          {p.bio && <p className="text-text-muted leading-relaxed mt-6">{p.bio}</p>}
          <PublicSocialLinks links={p.social_links} />

          <div className="mt-8 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-2">Identity</p>
              <p className="text-sm">{[p.gender, p.orientation].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-2">Looking for</p>
              <div className="flex flex-wrap gap-2">
                {(p.relationship_goals || []).map((g) => <span key={g} className="text-xs uppercase tracking-[0.15em] text-gold border border-wine/30 px-3 py-1 rounded-full">{g}</span>)}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-2">Interests</p>
              <div className="flex flex-wrap gap-2">
                {(p.kinks || []).map((k) => <span key={k} className="text-xs uppercase tracking-[0.15em] text-text-muted border border-white/10 px-3 py-1 rounded-full">{k}</span>)}
              </div>
            </div>
          </div>

          {lb && (lb.total_gifted > 0 || lb.last_week_crown) && (
            <div data-testid="gift-leaderboard" className="mt-8 rounded-2xl border border-gold/25 bg-gold/5 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Trophy size={14} /> Top admirers this week</p>
                <p data-testid="lifetime-gifted" className="flex items-center gap-1.5 text-xs text-text-muted">
                  <Gift size={13} className="text-gold" /> {dollars(lb.total_gifted)} gifted lifetime
                </p>
              </div>
              <p data-testid="leaderboard-resets" className="text-[11px] text-text-muted mb-4">Board resets {resetsIn(lb.resets_at)} · everyone starts from zero each Monday</p>
              {lb.last_week_crown && (
                <div data-testid="last-week-crown" className="flex items-center gap-3 rounded-xl border border-gold/40 bg-ink/40 px-3 py-2.5 mb-4">
                  <Crown size={16} className="text-gold shrink-0" />
                  {lb.last_week_crown.photo_url && <img src={lb.last_week_crown.photo_url} alt="" className={`w-7 h-7 rounded-full object-cover border border-gold/40 ${lb.last_week_crown.discreet_mode ? "blur-sm" : ""}`} />}
                  <p className="text-sm flex-1"><span className="text-gold">{lb.last_week_crown.name}</span> <span className="text-text-muted text-xs">wore last week's crown</span></p>
                  <span className="text-xs text-gold">{dollars(lb.last_week_crown.total)}</span>
                </div>
              )}
              {lb.top.length === 0 && <p data-testid="leaderboard-empty" className="text-sm text-text-muted">No gifts yet this week — the crown is wide open.</p>}
              <div className="space-y-3">
                {lb.top.map((t, i) => (
                  <div key={t.user_id} data-testid={`top-gifter-${i + 1}`} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-medium ${RANK_STYLE[i].ring}`}>{RANK_STYLE[i].label}</span>
                    {t.photo_url && <img src={t.photo_url} alt="" className={`w-8 h-8 rounded-full object-cover border border-white/10 ${t.discreet_mode ? "blur-sm" : ""}`} />}
                    <span className="text-sm flex-1">{t.name}</span>
                    <span className="text-xs text-text-muted">{t.count} gift{t.count > 1 ? "s" : ""}</span>
                    <span className="text-sm text-gold">{dollars(t.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            <button data-testid="detail-message" onClick={() => nav(`/messages/${p.id}`)} className="flex items-center gap-2 px-8 py-3.5 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.15em]">
              <MessageCircle size={16} /> Message
            </button>
            <button data-testid="detail-like" onClick={like} className={`flex items-center gap-2 px-6 py-3.5 border rounded-full text-sm uppercase tracking-[0.15em] transition-colors ${p.liked_by_me ? "border-wine bg-wine/20" : "border-white/15 hover:border-wine"}`}>
              <Heart size={16} fill={p.liked_by_me ? "currentColor" : "none"} /> {p.liked_by_me ? "Admiring" : "Admire"}
            </button>
            <button data-testid="detail-superlike" onClick={superLike} className="flex items-center gap-2 px-6 py-3.5 border border-gold/40 text-gold rounded-full text-sm uppercase tracking-[0.15em] transition-colors hover:bg-gold/10">
              <Sparkles size={16} /> Super
            </button>
            {p.private_available && p.private_rate >= 299 && (
              <button data-testid="detail-private" onClick={() => setPrivOpen(true)} className="flex items-center gap-2 px-6 py-3.5 border border-wine/50 text-gold rounded-full text-sm uppercase tracking-[0.15em] transition-colors hover:bg-wine/10">
                <Lock size={15} /> Private · {dollars(p.private_rate)}/min
              </button>
            )}
            <button data-testid="detail-block" onClick={block} className="p-3.5 border border-white/10 rounded-full hover:border-white/30 transition-colors text-text-muted"><Ban size={16} /></button>
            <button data-testid="detail-report" onClick={report} className="p-3.5 border border-white/10 rounded-full hover:border-white/30 transition-colors text-text-muted"><Flag size={16} /></button>
          </div>
        </motion.div>
      </div>
      <PrivateConfirmModal open={privOpen} onClose={() => setPrivOpen(false)} user={p} />
    </div>
  );
}
